import { useState, useCallback, useContext, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getCount, VIEW_TO_STORE_KEY_MAP } from "@/other/utils/storeCountUtil.jsx";

const useViewsMenu = () => {
  const { project, isAppLoaded, viewManager, user } = useContext(BryntumContext);
  const [isEditMode, setIsEditMode] = useState(false)
  const [columns, setColumns] = useState([]);
  const [items, setItems] = useState([]);
  // Initialize original states with deep copies
  const [draggedItem, setDraggedItem] = useState(null)
  const [dragOverItem, setDragOverItem] = useState(null)

  const getViewCount = useCallback((isDefaultView, viewType) => {
    let recordsCount = 0;
    const store = project.getCrudStore(VIEW_TO_STORE_KEY_MAP[viewType]);
    if (store && isDefaultView) {
      recordsCount = getCount(store, viewType)
    }
    return recordsCount
  }, [project]);

  const loadColumnsAndItems = useCallback(() => {
    const folders = project.viewsFolderStore.query(() => true, true)
      .map((folder) => ({
        id: folder.id,
        name: folder.name
      }));
    const views = project.viewsStore.query(() => true, true)
      .map((view) => ({
        id: view.id,
        name: view.name,
        description: view.viewDescription,
        iconId: view.iconId,
        isHidden: !view.isVisible,
        columnId: view.folderId,
        color: view.color,
        order: view.order,
        count: getViewCount(view.isDefaultView, view.viewType),
        isDefaultView: view.isDefaultView,
        viewType: view.viewType,
      }));
    setColumns(folders);
    setItems(views);
  }, [project, getViewCount])

  const handleDragStart = useCallback(
    (e, itemId, columnId, order) => {
      const item = items.find((i) => i.id === itemId)
      const isFromHidden = item?.isHidden || false

      setDraggedItem({
        itemId,
        sourceColumnId: columnId,
        sourceOrder: order,
        isFromHidden,
      })
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setData("text/plain", itemId)
    },
    [items],
  )

  const handleDragOver = useCallback(
    (e, columnId, targetOrder, isHiddenSection = false) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      setDragOverItem({ columnId, targetOrder, isHiddenSection })
    },
    [],
  )

  const handleDragLeave = useCallback(() => {
    setDragOverItem(null)
  }, [])

  const handleDrop = useCallback((e, targetColumnId, targetOrder, isHiddenSection = false) => {
    e.preventDefault()
    setDragOverItem(null)

    if (!draggedItem) return

    const { itemId: movedItemId, sourceOrder, sourceColumnId, isFromHidden } = draggedItem

    setItems((prevItems) => {
      const newItems = [...prevItems]
      const movedItem = newItems.find((item) => item.id === movedItemId)

      if (!movedItem) return prevItems

      // Determine source and target locations
      const sourceLocationKey = isFromHidden ? "hidden" : `${sourceColumnId}-visible`
      const targetLocationKey = isHiddenSection ? "hidden" : `${targetColumnId}-visible`

      // Check if we're moving within the same location
      const isSameLocation = sourceLocationKey === targetLocationKey

      // If dropping in the same location and the target position would result in no change, do nothing
      if (isSameLocation) {
        // If dragging an item to its own position or the position right after it, no change needed
        if (targetOrder === sourceOrder || targetOrder === sourceOrder + 1) {
          return prevItems
        }
      }

      // Update item properties based on drop target
      if (isHiddenSection) {
        // Moving to hidden section
        movedItem.isHidden = true
        // Keep the original columnId for restoration later
      } else {
        // Moving to visible column
        movedItem.columnId = targetColumnId;
        movedItem.isHidden = false
      }

      // Get all items in the target location (excluding the moved item)
      const targetLocationItems = newItems
        .filter((item) => {
          const itemLocationKey = item.isHidden ? "hidden" : `${item.columnId}-visible`
          return itemLocationKey === targetLocationKey && item.id !== movedItemId
        })
        .sort((a, b) => a.order - b.order)

      if (isSameLocation) {
        // Moving within the same location
        // Calculate the effective target position (accounting for the item being removed first)
        let effectiveTargetOrder = targetOrder
        if (sourceOrder < targetOrder) {
          // If moving down, the target position shifts by 1 because we remove the item first
          effectiveTargetOrder = targetOrder - 1
        }

        // Insert the moved item at the effective target position
        targetLocationItems.splice(effectiveTargetOrder, 0, movedItem)

        // Reassign sequential orders
        targetLocationItems.forEach((item, index) => {
          item.order = index
        })
      } else {
        // Moving between different locations
        // Set the moved item's order to the target position
        movedItem.order = targetOrder

        // Adjust orders of items in the target location that are at or after the target position
        targetLocationItems.forEach((item) => {
          if (item.order >= targetOrder) {
            item.order += 1
          }
        })

        // Reindex the source location to fill the gap
        const sourceLocationItems = newItems
          .filter((item) => {
            const itemLocationKey = item.isHidden ? "hidden" : `${item.columnId}-visible`
            return itemLocationKey === sourceLocationKey && item.id !== movedItemId
          })
          .sort((a, b) => a.order - b.order)

        sourceLocationItems.forEach((item, index) => {
          item.order = index
        })
      }

      return newItems
    })

    setDraggedItem(null)
  },
  [draggedItem])

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null)
    setDragOverItem(null)
  }, [])

  const updateColumnName = useCallback((columnId, newName) => {
    setColumns((prevColumns) => prevColumns.map((col) => (col.id === columnId ? { ...col, name: newName } : col)))
  }, [])

  const updateMenuItem = useCallback((itemId, field, value) => {
    setItems((prevItems) => prevItems.map((item) => (item.id === itemId ? { ...item, [field]: value } : item)))
  }, [])

  const setItemPendingDelete = useCallback((itemId, isPendingDelete) => {
    setItems((prevItems) => prevItems.map((item) => (
      item.id === itemId ? { ...item, isPendingDelete } : item
    )));
  }, [])

  const handleSave = useCallback(() => {
    project.beginBatch();
    items.forEach((item) => {
      if (item.isPendingDelete) return;
      const viewRecord = project.viewsStore.getById(item.id);
      if (viewRecord) {
        // Update existing view
        viewRecord.name = item.name;
        viewRecord.viewDescription = item.description;
        viewRecord.iconId = item.iconId;
        viewRecord.isVisible = !item.isHidden;
        viewRecord.color = item.color;
        viewRecord.order = item.order;
        viewRecord.folderId = item.columnId;
      }
    })
    columns.forEach((column) => {
      const folderRecord = project.viewsFolderStore.getById(column.id);
      if (folderRecord) {
        // Update existing folder
        folderRecord.name = column.name;
      }
    })
    items
      .filter((item) => item.isPendingDelete)
      .forEach((item) => {
        const viewRecord = project.viewsStore.getById(item.id);
        if (viewRecord?.canBeDeletedBy(user)) {
          viewManager?.deleteView(viewRecord);
        }
      });
    project.endBatch();
    loadColumnsAndItems();
    setIsEditMode(false)
  }, [setIsEditMode, items, project, columns, viewManager, loadColumnsAndItems, user])

  const handleCancel = useCallback(() => {
    loadColumnsAndItems();
    setIsEditMode(false)
  }, [setIsEditMode, loadColumnsAndItems])

  useEffect(() => {
    if (isAppLoaded) {
      loadColumnsAndItems();
    }
  }, [isAppLoaded, loadColumnsAndItems]);

  return {
    isEditMode,
    setIsEditMode,
    columns,
    items,
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleSave,
    handleCancel,
    updateColumnName,
    updateMenuItem,
    setDragOverItem,
    setItemPendingDelete,
  }
}

export default useViewsMenu;
